const { debounce, trackGlobalSearchResults, trackGlobalSearch } = window.themeUtils;

if (!customElements.get("predictive-search")) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.cachedResults = new Map();
      this.input = this.querySelector("[data-ps-input]");
      this.mountAttribute = this.getAttribute("mount-attribute") || "data-ps-mount";
      this.predictiveSearchResults = document.querySelector(`[${this.mountAttribute}]`);
      this.form = this.querySelector("form.ps_search");
      this.headerCta = document.querySelector(".header__cta");
      this.suppressEscapeKeyup = false;

      this.isOpen = false;
      this.activeIndex = -1;

      this.sectionId = this.getAttribute("section-id") || "predictive-search";
      this.minChars = Number(this.getAttribute("min-chars") || 2);
      this.resourceTypes = this.getAttribute("resource-types") || "product,article,query";
      this.limit = Number(this.getAttribute("resource-limit") || 6);
      this.cacheTtl = Number(this.getAttribute("cache-ttl-ms") || 30000);
      this.source = this.getAttribute("track-source") || "header";

      this.controller = null;

      this.onInput = debounce(this.handleInput.bind(this), 300);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onKeyup = this.handleKeyup.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onMountClick = this.handleMountClick.bind(this);
      this.onOutsideClick = this.handleOutsideClick.bind(this);
      this.predictiveSearchResults.addEventListener("keydown", this.onKeydown);
    }

    connectedCallback() {
      if (!this.input || !this.predictiveSearchResults) return;

      this.input.addEventListener("input", this.onInput);
      this.input.addEventListener("keydown", this.onKeydown);
      this.form?.addEventListener("submit", this.onSubmit);
      this.predictiveSearchResults.addEventListener("click", this.onMountClick);
      document.addEventListener("keydown", this.onDocumentKeydown);
      document.addEventListener("keyup", this.onKeyup, true);
      document.addEventListener("click", this.onOutsideClick);
      document.addEventListener("focusin", this.onFocusIn);
    }

    disconnectedCallback() {
      this.input?.removeEventListener("input", this.onInput);
      this.input?.removeEventListener("keydown", this.onKeydown);
      this.form?.removeEventListener("submit", this.onSubmit);
      this.predictiveSearchResults?.removeEventListener("click", this.onMountClick);
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("keydown", this.onDocumentKeydown);
      document.removeEventListener("keyup", this.onKeyup, true);
      document.removeEventListener("click", this.onOutsideClick);
      document.removeEventListener("focusin", this.onFocusIn);

      this.controller?.abort();
    }

    getQuery() {
      return this.input.value.trim();
    }

    buildUrl(term) {
      const params = new URLSearchParams();

      params.set("q", term);
      params.set("resources[type]", this.resourceTypes);
      params.set("resources[limit]", String(this.limit));
      params.set("resources[options][unavailable_products]", "last");
      params.set("section_id", this.sectionId);

      return `${window.Shopify?.routes?.root || "/"}search/suggest?${params}`;
    }

    handleDocumentKeydown(event) {
      if (event.key !== "Escape") return;
      const target = event.target;
      if ( this.contains(target) || this.predictiveSearchResults?.contains(target) ) {
        return;
      }

      this.handleKeydown(event);
    }

    handleFocusIn(event) {
      if (!this.isOpen) return;
    
      const target = event.target;
    
      const insideSearch = this.contains(target);
      const insideResults = this.predictiveSearchResults?.contains(target);
    
      if (insideSearch || insideResults) {
        return;
      }
    
      this.close();
      this.form?.classList.remove("is-open");
      this.input.value = "";
    }

    async handleInput() {
      const term = this.getQuery();

      if (term.length < this.minChars) {
        this.close();
        return;
      }

      await this.getSearchResults(term);
    }

    async getSearchResults(term) {
      const url = this.buildUrl(term);

      this.controller?.abort();
      this.controller = new AbortController();

      this.setAttribute("data-state", "loading");
      this.setAttribute("aria-busy", "true");

      const cached = this.cachedResults.get(url);

      if (cached && Date.now() - cached.at < this.cacheTtl) {
        this.renderSearchResults(cached.html, term);
        return;
      }

      try {
        const response = await fetch(url, {
          signal: this.controller.signal
        });

        if (!response.ok) {
          throw new Error(`Predictive search failed: ${response.status}`);
        }

        const html = await response.text();

        if (this.controller.signal.aborted) return;

        this.cachedResults.set(url, {
          html,
          at: Date.now()
        });

        this.renderSearchResults(html, term);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;

        console.error("[predictive-search]", error);

        this.setAttribute("data-state", "error");
        this.setAttribute("aria-busy", "false");
      }
    }

    renderSearchResults(html, term) {
      const documentFragment = new DOMParser().parseFromString(html, "text/html");
      const section = documentFragment.querySelector("#shopify-section-predictive-search");

      if (!section) {
        console.error("[predictive-search] predictive search section not found");
        return;
      }

      this.predictiveSearchResults.innerHTML = section.innerHTML;
      this.activeIndex = -1;

      this.setAttribute("data-state", "ready");
      this.setAttribute("aria-busy", "false");

      this.open();

      const count = this.results().length;
      trackGlobalSearchResults(term, count);
    }

    results() {
      return Array.from(
        this.predictiveSearchResults.querySelectorAll("[data-ps-result]")
      );
    }

    open() {
      this.isOpen = true;
      this.predictiveSearchResults.hidden = false;
      this.setAttribute("open", "");
      this.input.setAttribute("aria-expanded", "true");
    }

    close(clearSearchTerm = false) {
      this.isOpen = false;

      this.removeAttribute("open");
      this.removeAttribute("data-state");

      this.input.setAttribute("aria-expanded", "false");
      this.input.setAttribute("aria-activedescendant", "");

      this.activeIndex = -1;

      this.predictiveSearchResults.hidden = true;

      if (clearSearchTerm) {
        this.input.value = "";
      }
    }

    closeResults() {
      this.predictiveSearchResults.hidden = true;
      this.input.setAttribute("aria-activedescendant", "");
      this.activeIndex = -1;
    }

    getFocusableResults() {
      return this.results()
        .map((item) => {
          if ( item.matches("a, button, input, select, textarea, [tabindex]:not([tabindex='-1'])")) {
            return item;
          }
    
          return item.querySelector(
            "a, button, input, select, textarea, [tabindex]:not([tabindex='-1'])"
          );
        })
        .filter(Boolean);
    }

    handleKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
      
        if (this.isOpen) {
          if (!this.predictiveSearchResults.hidden) {
            this.suppressEscapeKeyup = true;
            this.closeResults();
            this.input.focus();
          } else {
            this.suppressEscapeKeyup = false;
            this.close();
          }
        }
      
        return;
      }

      if (!this.isOpen) return;

      const items = this.results();
      if (!items.length) return;

      if (event.key === "Tab" && !event.shiftKey) {
        const focusableResults = this.getFocusableResults();
      
        if (!focusableResults.length) return;
      
        const lastResult = focusableResults[focusableResults.length - 1];
      
        if (event.target === lastResult) {
          const headerCta = this.headerCta?.querySelector("a, button");
      
          if (headerCta) {
            event.preventDefault();
            headerCta.focus();
          }
        }
      
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
      
        const direction = event.key === "ArrowDown" ? 1 : -1;
      
        this.activeIndex =
          (this.activeIndex + direction + items.length) % items.length;
      
        const activeItem = items[this.activeIndex];
      
        const focusTarget =
          activeItem.querySelector("a, button, [tabindex]") || activeItem;
      
        focusTarget.focus();
      
        return;
      }

      if (event.key === "Enter" && this.activeIndex > -1) {
        event.preventDefault();

        const link =
          items[this.activeIndex].querySelector("a") ||
          items[this.activeIndex];

        link.click();
      }
    }

    handleKeyup(event) {
      if (event.key !== "Escape" || !this.isOpen) return;
    
      if (this.suppressEscapeKeyup) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressEscapeKeyup = false;
      }
    }

    handleSubmit() {
      trackGlobalSearch(this.getQuery(), this.source);
    }

    handleMountClick(event) {
      event.stopPropagation();

      const result = event.target.closest("[data-ps-result]");
      if (!result) return;
    }

    handleOutsideClick(event) {
      const target = event.target;

      const clickedInsideSearch = this.contains(target);
      const clickedInsideResults = this.predictiveSearchResults?.contains(target);

      if (!clickedInsideSearch && !clickedInsideResults) {
        this.close();
      }
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
}